import { Form, InputNumber, Radio } from 'antd';
import type { FormInstance } from 'antd';
import type {
  CategoryRateRule,
  SeriesRateRule,
  ConversionBonusTier,
  DailyClassAdjustmentTier,
  SalaryComponents,
  VolumeBonusTier,
} from '@salaries/shared';

// Shared flat form shape for the salary components + helpers to convert to/from
// the typed SalaryComponents. Used by BOTH the global defaults section and the
// per-staff "custom" sections so the inputs stay identical. (Sales bonus
// removed — class-based salary only.)

export interface ComponentFlat {
  base_type: 'none' | 'fixed' | 'hourly';
  base_monthly_amount: number;
  // Only meaningful for base_type='hourly' (time_clock mechanism only — no UI
  // control for it yet, see BaseFields; wired once the settings UI ships).
  base_hourly_rate: number;
  // One 3-way choice for the weekend bonus: off / percent uplift / fixed ₪ per
  // session. Replaces the old separate enabled + mode fields.
  weekend_choice: 'none' | 'percent' | 'fixed';
  weekend_friday_percent: number;
  weekend_saturday_percent: number;
  weekend_friday_amount: number;
  weekend_saturday_amount: number;
  travel_type: 'none' | 'fixed_monthly' | 'per_workday' | 'per_km';
  travel_amount: number;
  // per_km travel: the box's ₪/km rate. Per-location distances are per-staff and
  // edited separately (TravelKmLocationsFields), not in this flat form.
  travel_km_rate: number;
  // Conversion bonus (global box settings only — the per-coach drawer overrides
  // just the ladder). enabled + window + the default ladder (edited by
  // ConversionTiersInput, a normal form control over the array).
  conversion_enabled: boolean;
  conversion_tiers: ConversionBonusTier[];
  conversion_window_days: number;
  // Empty-class flat pay: on/off + ₪ per empty class.
  empty_class_pay_enabled: boolean;
  empty_class_pay_amount: number;
  // בונוס מדרגות: on/off + the marginal ₪-per-class ladder (edited by
  // VolumeTiersInput, which is a normal form control over the array).
  volume_bonus_enabled: boolean;
  volume_bonus_tiers: VolumeBonusTier[];
  // התאמת שכר לפי כמות שיעורים ביום: on/off + the ± ₪-per-class ladder keyed by
  // daily count (edited by DailyTiersInput, a normal form control over the array).
  daily_class_adjustment_enabled: boolean;
  daily_class_adjustment_tiers: DailyClassAdjustmentTier[];
  // Cancelled-class pay: on/off + the catch-all % (per-reason percentages are a
  // list and are edited/saved separately — see CancelledClassPayCard).
  cancelled_class_pay_enabled: boolean;
  cancelled_class_default_percent: number;
  // קטגוריה rates: the whole list is one field, edited by CategoryRateFields.
  category_rates: CategoryRateRule[];
  series_rates: SeriesRateRule[];
}

export const DEFAULT_COMPONENT_FLAT: ComponentFlat = {
  base_type: 'none',
  base_monthly_amount: 0,
  base_hourly_rate: 0,
  weekend_choice: 'none',
  weekend_friday_percent: 0,
  weekend_saturday_percent: 0,
  weekend_friday_amount: 0,
  weekend_saturday_amount: 0,
  travel_type: 'none',
  travel_amount: 0,
  travel_km_rate: 0,
  conversion_enabled: false,
  conversion_tiers: [],
  conversion_window_days: 0,
  empty_class_pay_enabled: false,
  empty_class_pay_amount: 0,
  volume_bonus_enabled: false,
  volume_bonus_tiers: [],
  daily_class_adjustment_enabled: false,
  daily_class_adjustment_tiers: [],
  cancelled_class_pay_enabled: false,
  cancelled_class_default_percent: 0,
  category_rates: [],
  series_rates: [],
};

export function componentsToFlat(c: SalaryComponents): ComponentFlat {
  return {
    base_type: c.base_salary.type,
    base_monthly_amount: c.base_salary.type === 'fixed' ? c.base_salary.monthly_amount : 0,
    base_hourly_rate: c.base_salary.type === 'hourly' ? c.base_salary.hourly_rate : 0,
    weekend_choice: !c.weekend_bonus.enabled ? 'none' : c.weekend_bonus.mode,
    weekend_friday_percent: c.weekend_bonus.friday_percent,
    weekend_saturday_percent: c.weekend_bonus.saturday_percent,
    weekend_friday_amount: c.weekend_bonus.friday_amount,
    weekend_saturday_amount: c.weekend_bonus.saturday_amount,
    travel_type: c.travel.type,
    travel_amount:
      c.travel.type === 'fixed_monthly'
        ? c.travel.monthly_amount
        : c.travel.type === 'per_workday'
          ? c.travel.per_day_amount
          : 0,
    travel_km_rate: c.travel.type === 'per_km' ? c.travel.rate_per_km : 0,
    conversion_enabled: c.conversion_bonus.enabled,
    conversion_tiers: c.conversion_bonus.tiers ?? [],
    conversion_window_days: c.conversion_bonus.window_days,
    empty_class_pay_enabled: c.empty_class_pay.enabled,
    empty_class_pay_amount: c.empty_class_pay.amount,
    volume_bonus_enabled: c.volume_bonus?.enabled ?? false,
    volume_bonus_tiers: c.volume_bonus?.tiers ?? [],
    daily_class_adjustment_enabled: c.daily_class_adjustment?.enabled ?? false,
    daily_class_adjustment_tiers: c.daily_class_adjustment?.tiers ?? [],
    cancelled_class_pay_enabled: c.cancelled_class_pay?.enabled ?? false,
    cancelled_class_default_percent: c.cancelled_class_pay?.default_percent ?? 0,
    category_rates: c.category_rates ?? [],
    series_rates: c.series_rates ?? [],
  };
}

export function flatToComponents(f: ComponentFlat): SalaryComponents {
  const base_salary: SalaryComponents['base_salary'] =
    f.base_type === 'fixed'
      ? { type: 'fixed', monthly_amount: f.base_monthly_amount || 0 }
      : f.base_type === 'hourly'
        ? { type: 'hourly', hourly_rate: f.base_hourly_rate || 0 }
        : { type: 'none' };

  const travel: SalaryComponents['travel'] =
    f.travel_type === 'fixed_monthly'
      ? { type: 'fixed_monthly', monthly_amount: f.travel_amount || 0 }
      : f.travel_type === 'per_workday'
        ? { type: 'per_workday', per_day_amount: f.travel_amount || 0 }
        : f.travel_type === 'per_km'
          ? { type: 'per_km', rate_per_km: f.travel_km_rate || 0 }
          : { type: 'none' };

  return {
    base_salary,
    weekend_bonus: {
      enabled: f.weekend_choice !== 'none',
      mode: f.weekend_choice === 'fixed' ? 'fixed' : 'percent',
      friday_percent: f.weekend_friday_percent || 0,
      saturday_percent: f.weekend_saturday_percent || 0,
      friday_amount: f.weekend_friday_amount || 0,
      saturday_amount: f.weekend_saturday_amount || 0,
    },
    travel,
    conversion_bonus: {
      enabled: !!f.conversion_enabled,
      tiers: f.conversion_tiers ?? [],
      window_days: f.conversion_window_days || 0,
    },
    empty_class_pay: {
      enabled: !!f.empty_class_pay_enabled,
      amount: f.empty_class_pay_amount || 0,
    },
    volume_bonus: {
      enabled: !!f.volume_bonus_enabled,
      // Kept even while off, so switching the bonus back on doesn't lose the
      // ladder that was typed.
      tiers: f.volume_bonus_tiers ?? [],
    },
    daily_class_adjustment: {
      enabled: !!f.daily_class_adjustment_enabled,
      // Kept even while off, so switching it back on doesn't lose the ladder
      // that was typed.
      tiers: f.daily_class_adjustment_tiers ?? [],
    },
    cancelled_class_pay: {
      enabled: !!f.cancelled_class_pay_enabled,
      default_percent: f.cancelled_class_default_percent || 0,
    },
    category_rates: f.category_rates ?? [],
    series_rates: f.series_rates ?? [],
  };
}

// ── Section field components (read live values via the passed form) ───────────

/** `allowHourly` offers a rate × clocked-hours option — only meaningful for the
 *  time_clock mechanism (hours come from Arbox's employeeAttendanceReport, not
 *  from any group_classes/hugim data source). */
export function BaseFields({ form, allowHourly = false }: { form: FormInstance; allowHourly?: boolean }) {
  const baseType = Form.useWatch('base_type', form);
  return (
    <Form.Item name="base_type" className="mb-0">
      <Radio.Group className="flex flex-col gap-3">
        <Radio value="none">ללא</Radio>
        <Radio value="fixed">
          <span className="inline-flex items-center gap-2 flex-wrap">
            סכום חודשי קבוע
            <Form.Item name="base_monthly_amount" noStyle>
              <InputNumber min={0} style={{ width: 120 }} dir="ltr" disabled={baseType !== 'fixed'} />
            </Form.Item>
            ₪
          </span>
        </Radio>
        {allowHourly && (
          <Radio value="hourly">
            <span className="inline-flex items-center gap-2 flex-wrap">
              תעריף לשעה × שעות נוכחות בפועל
              <Form.Item name="base_hourly_rate" noStyle>
                <InputNumber min={0} style={{ width: 120 }} dir="ltr" disabled={baseType !== 'hourly'} />
              </Form.Item>
              ₪ לשעה
            </span>
          </Radio>
        )}
      </Radio.Group>
    </Form.Item>
  );
}

// One vertical radio (ללא / אחוז מהשכר / סכום קבוע לשיעור) — the chosen option
// reveals its two Friday/Saturday fields right beneath it. Percent = a % uplift
// on the Arbox session pay; fixed = a flat ₪ added per weekend session.
//
// showEnabled (box defaults): the "ללא" option is offered. In the per-staff
// drawer (showEnabled=false) the source select already drives on/off, so only
// the percent/fixed choice shows and the consumer forces enabled=true on save.
export function WeekendFields({
  form,
  showEnabled = true,
}: {
  form: FormInstance;
  showEnabled?: boolean;
}) {
  const choice: ComponentFlat['weekend_choice'] = Form.useWatch('weekend_choice', form) ?? 'none';

  // Indent the revealed inputs under the option label (RTL: pad from the right).
  const percentInputs = (
    <div className="flex items-end gap-4 flex-wrap pr-6">
      <Form.Item name="weekend_friday_percent" label="אחוז תוספת לשישי (%)" className="mb-0">
        <InputNumber min={0} max={100} style={{ width: 140 }} dir="ltr" />
      </Form.Item>
      <Form.Item name="weekend_saturday_percent" label="אחוז תוספת לשבת (%)" className="mb-0">
        <InputNumber min={0} max={100} style={{ width: 140 }} dir="ltr" />
      </Form.Item>
    </div>
  );

  const fixedInputs = (
    <div className="flex items-end gap-4 flex-wrap pr-6">
      <Form.Item name="weekend_friday_amount" label="תוספת קבועה לשישי (₪)" className="mb-0">
        <InputNumber min={0} style={{ width: 140 }} dir="ltr" />
      </Form.Item>
      <Form.Item name="weekend_saturday_amount" label="תוספת קבועה לשבת (₪)" className="mb-0">
        <InputNumber min={0} style={{ width: 140 }} dir="ltr" />
      </Form.Item>
    </div>
  );

  // Per-staff "custom" mode: no "ללא" (the source select means active). A 'none'
  // seed displays as percent via getValueProps; the field stays registered so it's
  // collected on save, and the consumer forces enabled=true.
  if (!showEnabled) {
    const sel = choice === 'fixed' ? 'fixed' : 'percent';
    return (
      <Form.Item
        name="weekend_choice"
        className="mb-0"
        getValueProps={(v) => ({ value: v === 'fixed' ? 'fixed' : 'percent' })}
      >
        <Radio.Group className="flex flex-col gap-3">
          <Radio value="percent">אחוז מהשכר</Radio>
          {sel === 'percent' && percentInputs}
          <Radio value="fixed">סכום קבוע לשיעור (₪)</Radio>
          {sel === 'fixed' && fixedInputs}
        </Radio.Group>
      </Form.Item>
    );
  }

  return (
    <Form.Item name="weekend_choice" className="mb-0">
      <Radio.Group className="flex flex-col gap-3">
        <Radio value="none">ללא תוספת סוף שבוע</Radio>
        <Radio value="percent">אחוז מהשכר</Radio>
        {choice === 'percent' && percentInputs}
        <Radio value="fixed">סכום קבוע לשיעור (₪)</Radio>
        {choice === 'fixed' && fixedInputs}
      </Radio.Group>
    </Form.Item>
  );
}

// Empty-class flat pay. A class that occurred but had 0 of whatever the box pays
// for (its class_calc_method) is paid this flat ₪ instead of its normal pay.
//
// showEnabled (box defaults): the on/off radio is offered. In the per-staff drawer
// (showEnabled=false) the source select already drives on/off, so only the amount
// shows and the consumer forces enabled=true on save.
export function EmptyClassPayFields({
  form,
  showEnabled = true,
}: {
  form: FormInstance;
  showEnabled?: boolean;
}) {
  const enabled = Form.useWatch('empty_class_pay_enabled', form);
  const amount = (
    <Form.Item
      name="empty_class_pay_amount"
      label="סכום קבוע לשיעור ריק, במקום התשלום הרגיל (₪)"
      className="mb-0"
    >
      <InputNumber min={0} dir="ltr" style={{ width: 200 }} disabled={showEnabled && !enabled} />
    </Form.Item>
  );
  if (!showEnabled) return <div className="mt-1">{amount}</div>;
  return (
    <Form.Item name="empty_class_pay_enabled" className="mb-0">
      {/* OFF is NOT "don't pay for empty classes" — it means no special rule, so
          an empty class pays its normal salary like any other. Labelling it
          "ללא תשלום" read as the exact opposite of what it does. */}
      <Radio.Group className="flex flex-col gap-3">
        <Radio value={false}>תשלום רגיל על שיעורים ריקים, כמו כל שיעור אחר</Radio>
        <Radio value={true}>
          <div className="mt-2">{amount}</div>
        </Radio>
      </Radio.Group>
    </Form.Item>
  );
}

export function TravelFields({ form }: { form: FormInstance }) {
  const travelType: ComponentFlat['travel_type'] = Form.useWatch('travel_type', form) ?? 'none';
  // travel_amount is a single field shared by both paid types — render it inline
  // only inside the currently-selected option so it's never registered twice.
  const amount = (
    <>
      <Form.Item name="travel_amount" noStyle>
        <InputNumber min={0} style={{ width: 120 }} dir="ltr" />
      </Form.Item>
      ₪
    </>
  );
  // per_km: the box's ₪/km rate. The per-location one-way distances are personal
  // and edited per staff below (TravelKmLocationsFields). Pay per location =
  // rate × (distance × 2 round trip) × meeting-days at that location.
  const perKm = (
    <div className="mt-2 flex flex-col gap-1 pr-6">
      <Form.Item name="travel_km_rate" label="תעריף לקילומטר (₪)" className="mb-0">
        <InputNumber min={0} style={{ width: 140 }} dir="ltr" />
      </Form.Item>
      <span className="text-small text-secondary-text">
        המרחקים מוגדרים לכל איש צוות לפי סניף (הלוך ושוב, לפי ימי העבודה בסניף).
      </span>
    </div>
  );
  return (
    <Form.Item name="travel_type" className="mb-0">
      <Radio.Group className="flex flex-col gap-3">
        <Radio value="none">ללא</Radio>
        <Radio value="fixed_monthly">
          <span className="inline-flex items-center gap-2 flex-wrap">
            סכום חודשי קבוע
            {travelType === 'fixed_monthly' && amount}
          </span>
        </Radio>
        <Radio value="per_workday">
          <span className="inline-flex items-center gap-2 flex-wrap">
            לפי יום עבודה
            {travelType === 'per_workday' && amount}
          </span>
        </Radio>
        <Radio value="per_km">לפי קילומטר (הלוך ושוב, לפי ימי עבודה)</Radio>
        {travelType === 'per_km' && perKm}
      </Radio.Group>
    </Form.Item>
  );
}
